import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import { supabase } from '../utils/supabaseStorage';
import { AgentPreviewEndDrawer } from './AgentPreviewEndDrawer';
import type { AgentPricing } from '../types/agent';

const PAGE = 24;

type TemplateRow = {
  id: string;
  name: string;
  public_title: string | null;
  public_description: string | null;
  cover_media_url: string | null;
  cover_media_type: 'image' | 'video' | null;
  updated_at: string;
  pricing: AgentPricing | null;
  tags: string[] | null;
  tags_search?: string | null;
  /** Populated from Supabase embed (shape varies slightly in generated types). */
  agent_template_categories?: { agent_categories: { name: string; slug: string } | null }[] | null;
};

function escapeIlike(s: string): string {
  return s.replace(/\\/g, '\\\\').replace(/%/g, '\\%').replace(/_/g, '\\_');
}

const MAX_IN_FALLBACK = 200;

/**
 * If the RPC is unavailable, approximate search with OR across fields + tag array text + category id list.
 * Does not support multi-term AND the way the RPC does; single substring match only.
 */
async function fetchPageIlikeFallback(
  p: string,
  from: number,
  to: number,
  selectColumns: string
) {
  const { data: catMatch, error: catErr } = await supabase
    .from('agent_categories')
    .select('id')
    .or(`name.ilike.%${p}%,slug.ilike.%${p}%`)
    .limit(500);
  if (catErr) {
    console.warn('[PublicAgentsBrowse] category lookup for fallback', catErr);
  }
  const catIds = (catMatch || []).map((c: { id: string }) => c.id);
  let templateIdsFromCategory: string[] = [];
  if (catIds.length) {
    const { data: links, error: jErr } = await supabase
      .from('agent_template_categories')
      .select('agent_template_id')
      .in('category_id', catIds);
    if (jErr) {
      console.warn('[PublicAgentsBrowse] junction lookup for fallback', jErr);
    } else {
      templateIdsFromCategory = [
        ...new Set((links || []).map((l: { agent_template_id: string }) => l.agent_template_id)),
      ].slice(0, MAX_IN_FALLBACK);
    }
  }

  const orParts: string[] = [
    `name.ilike.%${p}%`,
    `public_title.ilike.%${p}%`,
    `public_description.ilike.%${p}%`,
    `tags_search.ilike.%${p}%`,
    `tags::text.ilike.%${p}%`,
  ];
  if (templateIdsFromCategory.length) {
    orParts.push(`id.in.(${templateIdsFromCategory.join(',')})`);
  }
  return await supabase
    .from('agent_templates')
    .select(selectColumns)
    .eq('is_active', true)
    .or(orParts.join(','))
    .order('updated_at', { ascending: false })
    .range(from, to);
}

function displayTitle(t: TemplateRow): string {
  return (t.public_title && t.public_title.trim()) || t.name;
}

function isFreeTemplate(t: TemplateRow): boolean {
  return Boolean(t.pricing && typeof t.pricing === 'object' && t.pricing.free === true);
}

type PublicAgentsBrowseProps = {
  searchInput: string;
};

export function PublicAgentsBrowse({ searchInput }: PublicAgentsBrowseProps) {
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [items, setItems] = useState<TemplateRow[]>([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const loadingRef = useRef(false);
  const [previewAgent, setPreviewAgent] = useState<TemplateRow | null>(null);
  const previewOpen = previewAgent !== null;

  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedSearch(
        searchInput
          .trim()
          .replace(/,/g, ' ')
          .replace(/\s+/g, ' ')
      );
    }, 200);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const fetchPage = useCallback(
    async (page: number): Promise<TemplateRow[]> => {
      const from = page * PAGE;
      const to = from + PAGE - 1;
      const selectColumns =
        'id, name, public_title, public_description, cover_media_url, cover_media_type, updated_at, pricing, tags, tags_search, agent_template_categories ( agent_categories ( name, slug ) )';

      if (debouncedSearch.length > 0) {
        const { data, error } = await supabase.rpc('search_active_agent_templates', {
          p_query: debouncedSearch,
          p_limit: PAGE,
          p_offset: from,
        });
        if (error) {
          const p = escapeIlike(debouncedSearch);
          if (p.length > 0) {
            const fb1 = await fetchPageIlikeFallback(p, from, to, selectColumns);
            let d2: TemplateRow[] | null = fb1.data as unknown as TemplateRow[] | null;
            let e2 = fb1.error;
            if (e2?.message && /tags::text|ilike|syntax/i.test(e2.message)) {
              const fb2 = await supabase
                .from('agent_templates')
                .select(selectColumns)
                .eq('is_active', true)
                .or(
                  `name.ilike.%${p}%,public_title.ilike.%${p}%,public_description.ilike.%${p}%,tags_search.ilike.%${p}%`
                )
                .order('updated_at', { ascending: false })
                .range(from, to);
              d2 = fb2.data as unknown as TemplateRow[] | null;
              e2 = fb2.error;
            }
            if (e2) {
              throw new Error(e2.message);
            }
            return d2 || [];
          }
          throw new Error(error.message);
        }
        return (data || []) as unknown as TemplateRow[];
      }

      const { data, error } = await supabase
        .from('agent_templates')
        .select(selectColumns)
        .eq('is_active', true)
        .order('updated_at', { ascending: false })
        .range(from, to);
      if (error) {
        throw new Error(error.message);
      }
      return (data || []) as unknown as TemplateRow[];
    },
    [debouncedSearch]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      setPageIndex(0);
      setHasMore(true);
      setItems([]);
      try {
        const rows = await fetchPage(0);
        if (cancelled) return;
        setItems(rows);
        setHasMore(rows.length === PAGE);
        setPageIndex(0);
      } catch (e) {
        if (!cancelled) {
          setLoadError(e instanceof Error ? e.message : 'Failed to load');
          setItems([]);
          setHasMore(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchPage]);

  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore || loadingRef.current) return;
    loadingRef.current = true;
    setLoadingMore(true);
    setLoadError(null);
    const next = pageIndex + 1;
    try {
      const rows = await fetchPage(next);
      if (rows.length) {
        setItems((prev) => [...prev, ...rows]);
        setPageIndex(next);
        setHasMore(rows.length === PAGE);
      } else {
        setHasMore(false);
      }
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load more');
    } finally {
      setLoadingMore(false);
      loadingRef.current = false;
    }
  }, [fetchPage, hasMore, loading, loadingMore, pageIndex]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          void loadMore();
        }
      },
      { root: null, rootMargin: '200px 0px', threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore, hasMore, loading]);

  return (
    <div className="w-full">
      <AgentPreviewEndDrawer
        agent={previewAgent}
        open={previewOpen}
        onClose={() => setPreviewAgent(null)}
      />
      {loadError && (
        <p className="mt-6 text-sm text-amber-200/90" role="status">
          {loadError}
        </p>
      )}

      {loading ? (
        <ul className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" aria-hidden>
          {Array.from({ length: 6 }).map((_, i) => (
            <li key={i}>
              <div className="relative aspect-[5/3] w-full overflow-hidden rounded-2xl border border-subtle bg-elevated">
                <div className="absolute inset-0 animate-pulse bg-overlay" />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-4 pb-5 pt-10">
                  <div className="h-8 w-4/5 animate-pulse rounded-md bg-white/20 sm:h-9" />
                </div>
              </div>
            </li>
          ))}
        </ul>
      ) : items.length === 0 ? (
        <p className="mt-12 text-center text-th-secondary">
          No agents match {debouncedSearch ? 'this search' : 'this view yet'}.
        </p>
      ) : (
        <>
          <ul className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((r) => (
              <li key={r.id}>
                <button
                  type="button"
                  onClick={() => setPreviewAgent(r)}
                  className="group relative aspect-[5/3] w-full overflow-hidden rounded-2xl border border-subtle bg-overlay text-left shadow-sm transition hover:border-brand/40 hover:shadow-lg"
                >
                  {r.cover_media_url && r.cover_media_type === 'image' ? (
                    <img
                      src={r.cover_media_url}
                      alt=""
                      className="absolute inset-0 h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]"
                    />
                  ) : r.cover_media_url && r.cover_media_type === 'video' ? (
                    <>
                      <video
                        src={r.cover_media_url}
                        className="absolute inset-0 h-full w-full object-cover opacity-95"
                        muted
                        playsInline
                        preload="metadata"
                      />
                      <span className="absolute right-2 top-2 z-20 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
                        Video
                      </span>
                    </>
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-brand/30 via-elevated to-overlay">
                      <Sparkles className="h-10 w-10 text-brand/80" />
                    </div>
                  )}
                  {isFreeTemplate(r) && (
                    <span className="absolute left-2 top-2 z-20 rounded-md bg-emerald-500/90 px-2 py-0.5 text-xs font-semibold text-white shadow-sm">
                      Free
                    </span>
                  )}
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-transparent" aria-hidden />
                  <div className="absolute inset-x-0 bottom-0 z-10 flex flex-col justify-end px-4 pb-5 pt-10 text-left">
                    <h2 className="line-clamp-2 text-2xl font-bold leading-tight tracking-tight text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.85)] [text-shadow:0_2px_4px_rgba(0,0,0,0.9),0_4px_12px_rgba(0,0,0,0.65),0_0_1px_rgba(0,0,0,1)] sm:text-3xl sm:leading-tight">
                      {displayTitle(r)}
                    </h2>
                  </div>
                </button>
              </li>
            ))}
          </ul>
          {hasMore && (
            <div
              ref={sentinelRef}
              className="mt-8 flex h-12 w-full items-center justify-center"
              aria-hidden
            >
              {loadingMore ? <Loader2 className="h-6 w-6 animate-spin text-brand" /> : null}
            </div>
          )}
        </>
      )}
    </div>
  );
}
